import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTableCellsTdTitleAttribute } from '@mylighthouse/prism-ember';
import PrismTable from '@mylighthouse/prism-ember/components/prism/table';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { tKey } from 'ember-intl';
import MarketSnapshotsTableTdWithVariance, {
  type MarketSnapshotsTableTdWithVarianceSignature,
} from 'lumen/components/market/snapshots/table/td-with-variance';
import type {
  MarketPerformanceDataItem,
  MarketPerformanceSubscription,
} from 'lumen/models';
import type {
  MarketPerformanceMetrics,
  MarketPerformanceTotals,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';

import styles from './table.module.css';

export interface MarketSnapshotsTableSignature {
  Args: {
    dataItems: MarketPerformanceDataItem[];
    isLoading?: boolean;
    onSort: (orderBy: string) => void;
    orderBy: string;
    totals?: MarketPerformanceTotals;
    varianceType: Variance;
    visibleMetricTypes: MetricType[];
  };
}

const METRIC_TYPE_TRANSLATIONS: Record<MetricType, string> = {
  bi_forecast: tKey('general.bi_forecast'),
  budget: tKey('general.budget'),
  finance_forecast: tKey('market.snapshots.metric_type.finance_forecast'),
  ly: tKey('market.snapshots.metric_type.total_ly'),
  net_sdly: tKey('market.snapshots.metric_type.net_sdly'),
  otb: tKey('general.otb'),
  rms_forecast: tKey('market.snapshots.metric_type.rms_forecast'),
  sdly: tKey('market.snapshots.metric_type.sdly'),
  user_forecast: tKey('market.snapshots.metric_type.user_forecast'),
};

interface MetricDisplayValue {
  adr: MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData'];
  occupancy: MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData'];
  revenue: MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData'];
  revpar: MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData'];
}

function buildMetricDisplayValue(
  totals: MarketPerformanceMetrics,
  metricType: MetricType,
  varianceType: Variance,
): MetricDisplayValue {
  const varianceKey = varianceType === 'absolute' ? 'absolute' : 'percent';
  const resolvedVarianceType =
    metricType === 'otb' ? VARIANCES.NONE : varianceType;

  return {
    adr: {
      value: totals.adr,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.adr[varianceKey],
    },
    occupancy: {
      isValuePercent: true,
      value: totals.occupancy,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.occupancy[varianceKey],
    },
    revenue: {
      value: totals.revenue,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revenue[varianceKey],
    },
    revpar: {
      value: totals.revpar,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revpar[varianceKey],
    },
  };
}

export default class MarketSnapshotsTable extends Component<MarketSnapshotsTableSignature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        hasRightBorder: true,
        isFixed: 'left',
        minWidth: 200,
        name: this.intl.t('general.name'),
        tdComponent: PrismTableCellsTdTitleAttribute,
        valuePath: 'name',
      },
      ...this.args.visibleMetricTypes.map(
        (metricType): CustomEmberTableColumn => {
          return {
            name: this.intl.t(METRIC_TYPE_TRANSLATIONS[metricType]),
            subcolumns: [
              {
                displayValuePath: `displayValue.${metricType}.occupancy`,
                name: this.intl.t('snapshots.columns.occ'),
                tdComponent: MarketSnapshotsTableTdWithVariance,
                textAlign: 'right',
                valuePath: `${metricType}.occupancy`,
              },
              {
                displayValuePath: `displayValue.${metricType}.adr`,
                name: this.intl.t('general.adr'),
                tdComponent: MarketSnapshotsTableTdWithVariance,
                textAlign: 'right',
                valuePath: `${metricType}.adr`,
              },
              {
                displayValuePath: `displayValue.${metricType}.revenue`,
                name: this.intl.t('general.revenue'),
                tdComponent: MarketSnapshotsTableTdWithVariance,
                textAlign: 'right',
                valuePath: `${metricType}.revenue`,
              },
              {
                displayValuePath: `displayValue.${metricType}.revpar`,
                hasRightBorder: true,
                name: this.intl.t('general.revpar'),
                tdComponent: MarketSnapshotsTableTdWithVariance,
                textAlign: 'right',
                valuePath: `${metricType}.revpar`,
              },
            ],
            valuePath: '',
          };
        },
      ),
    ];
  }

  get footerRows(): CustomEmberTableRow[] {
    const { totals, visibleMetricTypes } = this.args;

    if (!totals) {
      return [];
    }

    const metricTypeToDisplayValues = visibleMetricTypes.reduce<
      Partial<Record<MetricType, MetricDisplayValue>>
    >((acc, metricType) => {
      const item = totals[metricType];
      if (!item) {
        return acc;
      }

      acc[metricType] = buildMetricDisplayValue(
        item.totals,
        metricType,
        this.args.varianceType,
      );

      return acc;
    }, {});

    return [
      {
        displayValue: metricTypeToDisplayValues,
        name: this.intl.t('general.total'),
      },
    ];
  }

  get rows(): CustomEmberTableRow[] {
    return this.subscriptions.map((subscription) => {
      const metricTypeToDisplayValues = subscription.dataItems.reduce<
        Partial<Record<MetricType, MetricDisplayValue>>
      >((acc, item) => {
        acc[item.metricType] = buildMetricDisplayValue(
          item.totals,
          item.metricType,
          this.args.varianceType,
        );

        return acc;
      }, {});

      return {
        displayValue: metricTypeToDisplayValues,
        name: subscription.hotelName,
      };
    });
  }

  get sorts(): { isAscending: boolean; valuePath: string }[] {
    const { orderBy } = this.args;
    const isDescending = orderBy.startsWith('-');

    return [
      {
        valuePath: isDescending ? orderBy.slice(1) : orderBy,
        isAscending: !isDescending,
      },
    ];
  }

  get subscriptions(): MarketPerformanceSubscription[] {
    return [...new Set(this.args.dataItems.map((item) => item.subscription))];
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    const sort = sorts[0];

    if (!sort) {
      // Empty array = ember-table's "unsorted" state. Toggle direction instead.
      const { orderBy } = this.args;
      const isDescending = orderBy.startsWith('-');

      this.args.onSort(isDescending ? orderBy.slice(1) : `-${orderBy}`);

      return;
    }

    const orderBy = sort.isAscending ? sort.valuePath : `-${sort.valuePath}`;

    this.args.onSort(orderBy);
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @footerRows={{this.footerRows}}
      @isLoading={{@isLoading}}
      @onUpdateSorts={{this.onUpdateSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @sorts={{this.sorts}}
      class={{styles.table}}
      data-test-table
    />
  </template>
}
